import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Icon } from '../icon';
import { Checkbox } from '../checkbox';
import Crown from '../svg/crown';
import { ZTooltip } from '../ztooltip';

const BulkSelectionButtons = styled.div`
    display: flex;
    margin-right: auto;
`;

const BulkSelectionButtonsList = styled.ul`
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    align-items: center;
`;

export type BulkButtonVariant = 'default' | 'destructive';

const BulkSelectionButton = styled.li.withConfig({
    shouldForwardProp: (prop) => prop !== 'variant'
})<{ $disabled?: boolean; variant?: BulkButtonVariant }>`
    margin-right: 25px;
    cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
    font-size: 14px;
    display: flex;
    align-items: center;

    &:not([aria-disabled='true']):hover {
        opacity: 0.7;
    }

    ${(props) =>
        props.$disabled &&
        `
        opacity: 0.5;
    `}

    ${(props) =>
        props.variant === 'destructive' &&
        `
        &&& {
            background-color: #fff5f5;
            border: 1px solid #fecaca;
            color: ${props.theme.app.component.button.destructive.default.backgroundColor};
            border-radius: 6px;
            padding: 0 10px;
            min-height: 28px;

            &:not([aria-disabled='true']):hover {
                background-color: #fee2e2;
                border-color: #fca5a5;
                color: ${props.theme.app.component.button.destructive.hover.backgroundColor};
                opacity: 1;
            }

            &:not([aria-disabled='true']):active {
                background-color: #fecaca;
                color: ${props.theme.app.component.button.destructive.active.backgroundColor};
            }
        }
    `}
`;

const BulkSectionButtonIcon = styled(Icon).withConfig({
    shouldForwardProp: (prop) => prop !== 'variant'
})<{ $isActive?: boolean; variant?: BulkButtonVariant }>`
    stroke-width: 0px;
    margin-right: 5px;

    color: ${(props) => (props.variant === 'destructive' ? 'inherit' : props.theme.input.color)};

    ${(props) =>
        props.$isActive &&
        `
        color: ${props.theme.app.secondary};
    `}
`;

const CrownUpgrade = styled(Icon)`
    margin-left: 5px;
    color: #f0047f;
`;

export interface BulkButton {
    key: string;
    tooltip?: string;
    label: string;
    icon?: React.ReactElement<React.SVGAttributes<SVGElement>>;
    isActive?: boolean;
    hidden?: boolean;
    isCheckbox?: boolean;
    checked?: boolean;
    disabled?: boolean;
    variant?: BulkButtonVariant;
    requireUpgrade?: boolean;
    onClick: (button: BulkButton) => void;
}

interface BulkSelectionSectionProps extends React.HTMLAttributes<HTMLDivElement> {
    buttons: BulkButton[];
}

interface BulkSelectionBarProps extends BulkSelectionSectionProps {
    children?: React.ReactNode;
    selectionCount?: number;
    selectionLabel?: (count: number) => string;
}

const BulkSelectionBarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    min-height: 28px;
    flex-wrap: wrap;

    ${BulkSelectionButtons} {
        margin-right: 0;
    }

    ${BulkSelectionButtonsList} {
        gap: 4px;
        flex-wrap: wrap;
    }

    ${BulkSelectionButton} {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        margin-right: 0;
        padding: 0 10px;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background: #ffffff;
        color: #263238;
        font-family: 'Inter', sans-serif;
        font-size: 12px;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        transition:
            background 0.15s,
            border-color 0.15s,
            color 0.15s;

        &:not([aria-disabled='true']):hover {
            background: #f9fafb;
            border-color: #d1d5db;
            opacity: 1;
        }
    }

    ${BulkSectionButtonIcon} {
        width: 16px;
        height: 16px;
        margin-right: 0;
        color: currentColor;
    }
`;

export const BulkSelectionCount = styled.div`
    display: inline-flex;
    align-items: center;
    margin-left: auto;
    height: 28px;
    padding: 0 10px;
    border-radius: 6px;
    color: #e8650a;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
`;

export const BulkSelectionSection: FC<BulkSelectionSectionProps> = ({ buttons, className, ...rest }) => {
    return (
        <BulkSelectionButtons className={className} {...rest}>
            <BulkSelectionButtonsList>
                {buttons.map((button) => (
                    <ZTooltip key={button.key} title={button.tooltip} position='top'>
                        <BulkSelectionButton
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!button.disabled) button.onClick(button);
                            }}
                            $disabled={button.disabled}
                            aria-disabled={button.disabled}
                            variant={button.variant}
                        >
                            {/* Normal version */}
                            {!button.isCheckbox && (
                                <>
                                    {button.icon && (
                                        <BulkSectionButtonIcon $isActive={button.isActive} variant={button.variant}>
                                            {button.icon}
                                        </BulkSectionButtonIcon>
                                    )}
                                    <span>{button.label}</span>
                                </>
                            )}

                            {/* Checkbox version */}
                            {button.isCheckbox && (
                                <Checkbox
                                    checked={button.checked ?? false}
                                    onChange={() => {
                                        if (!button.disabled) button.onClick(button);
                                    }}
                                    label={<span>{button.label}</span>}
                                />
                            )}

                            {/* Bulk operation requires upgrade */}
                            {button.requireUpgrade && (
                                <CrownUpgrade>
                                    <Crown />
                                </CrownUpgrade>
                            )}
                        </BulkSelectionButton>
                    </ZTooltip>
                ))}
            </BulkSelectionButtonsList>
        </BulkSelectionButtons>
    );
};

export const BulkSelectionBar: FC<BulkSelectionBarProps> = ({ buttons, children, className, selectionCount, selectionLabel, ...rest }) => {
    const selectionText =
        selectionCount != null
            ? selectionLabel
                ? selectionLabel(selectionCount)
                : `${selectionCount} ${selectionCount === 1 ? T._('row selected', 'Admin') : T._('rows selected', 'Admin')}`
            : null;

    return (
        <BulkSelectionBarContainer className={className} {...rest}>
            <BulkSelectionSection buttons={buttons} />
            {children}
            {selectionText != null && <BulkSelectionCount>{selectionText}</BulkSelectionCount>}
        </BulkSelectionBarContainer>
    );
};
